<template>
	<div class="relative isolate overflow-visible w-full max-w-container mx-auto">
		<div class="lg:hidden flex flex-col items-start gap-2 min-h-36 px-4 md:px-10">
			<motion.div
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(10px)' }"
				:transition="{ duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
				class="text-primary text-5xl tracking-tight font-semibold will-change-transform"
			>
				{{ $t('home.hero.title') }}
			</motion.div>
			<Motion
				as-child
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(25px)' }"
				:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
			>
				<UButton
					:label="$t('links.check-availability')"
					:to="localePath('/get-fiber')"
					size="lg"
					class="w-48 uppercase will-change-transform"
					variant="outline"
					color="primary"
				/>
			</Motion>
		</div>
		<UPageHero
			reverse
			:title="$t('home.hero.title')"
			orientation="horizontal"
			:description="$t('home.hero.description')"
			:ui="{
				container: 'flex flex-col items-center lg:grid py-24 md:py-32 lg:py-8 xl:py-24 gap-16 md:gap-x-0 md:gap-y-24 relative isolate overflow-visible',
				title: 'xl:-mt-24'
			}"
		>
			<template #title>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="hidden lg:block text-primary text-4xl lg:text-7xl tracking-tight font-bold lg:whitespace-nowrap will-change-transform"
				>
					{{ $t('home.hero.title') }}
				</motion.div>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
					class="block text-3xl lg:text-6xl text-pretty tracking-tight font-medium text-highlighted will-change-transform"
				>
					{{ $t('home.hero.subtitle1') }}
				</motion.div>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="block text-3xl lg:text-6xl text-pretty tracking-tight font-medium text-highlighted will-change-transform"
				>
					{{ $t('home.hero.subtitle2') }}
				</motion.div>
			</template>
			<template #description>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 0.8, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					{{ $t('home.hero.description') }}
				</motion.div>
			</template>
			<template #links>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(25px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
				>
					<SignupAddressChecker
						id="home-hero-address-checker"
						compact
						class="will-change-transform"
					/>
				</Motion>
			</template>

			<!-- OPTIMIZED: Remove Motion wrapper and animation delay for LCP -->
			<div class="max-w-[220px] sm:max-w-[320px] lg:max-w-[380px] xl:max-w-[519px] w-full lg:flex lg:items-end lg:justify-end -mt-4 lg:ml-auto lg:mt-42 xl:mt-16 contain-layout">
				<SvgImage
					id="svg-image-home-hero"
					fetchpriority="high"
					sizes="260px sm:320px lg:380px xl:519px"
					:image="{
						url: '/images/heroes/home.avif',
						alt: 'Maverix Fiber-fast internet',
						offsetY: -192,
						offsetX: 0,
						height: 117,
						width: 86.5,
						densities: 'x1 x2'
					}"
					:lines="[
						{
							rotation: -15,
							length: 150,
							direction: 'counter-clockwise',
							gap: 0
						}
					]"
					:circles="[
						{
							cx: 88,
							cy: 160,
							r: 120,
							fill: '#01629E'
						},
						{
							cx: 482,
							cy: 176,
							r: 18,
							fill: '#f98c03'
						}
					]"
					:viewbox="{
						width: 534,
						height: 534
					}"
					class="h-full w-full lg:min-h-[350px] pb-6 xl:pb-24 lg:pb-0 mt-0 -mb-3 sm:mt-8 sm:-mb-12 lg:mt-24 xl:mt-16 sm:mr-8 lg:mr-16 xl:mr-6 xl:-mb-24 shrink-0"
				/>
			</div>
		</UPageHero>
	</div>
</template>

<script setup lang="ts">
import {
	motion, Motion
} from 'motion-v'

const localePath = useLocalePath()
</script>
